import { VarsHelper } from "@swivl-mono/helpers";
import {
  CortexSetting,
  DISCOUNTS,
  DiscountDef,
  DiscountKey,
  LocationScope,
  LocationVarInheritance,
  LocationVarKey,
  MergedVarsWithKeys,
  VarDef,
  VarType
} from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, Input, TextArea, Tooltip } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { BiEditAlt } from "react-icons/bi";
import { GrRevert } from "react-icons/gr";

import { useMergedLocationVariables } from "../../../../../Hooks/useLocationVariables";
import { usePropertiesGetLocationIds } from "../../../../../Hooks/usePropertiesGetLocationIds";
import { CortexInheritancePill } from "../../Components/CortexInheritancePill";
import { revertLabelFor } from "../../Components/revertTarget";
import { TopicProvider, useTopicContext, useTopicHandlers, useTopicRow } from "../../Hooks/useTopicContext";

export const TOPIC_VARS: LocationVarKey[] = [
  ...DISCOUNTS.map((d) => d.detailsVarKey),
  ...DISCOUNTS.flatMap((d) => (d.nameVarKey ? [d.nameVarKey] : []))
];

/**
 * Per-discount examples. Generic placeholder copy teaches operators nothing —
 * each one shows the shape we want: amount, who qualifies, how to claim.
 */
const DETAILS_PLACEHOLDERS: Record<DiscountKey, string> = {
  [DiscountKey.Military]: "10% off every month with a valid military ID. Active duty and veterans. One discount per tenant.",
  [DiscountKey.Senior]: "10% off every month for tenants 62 and older. Show ID at signup.",
  [DiscountKey.Student]: "5% off every month with a current student ID. Verified once per rental term.",
  [DiscountKey.LongTerm]: "5% off after 12 consecutive months of on-time payment. Applied automatically.",
  [DiscountKey.Introductory]: "First month free on units 5x10 and larger. New tenants only.",
  [DiscountKey.Custom]: "Describe the discount: amount or percent off, who qualifies, and how to claim it."
};

/**
 * The built-in default for a var, as declared in VarDef — the value a field
 * falls back to when nothing is set at any scope. Same source the server merges
 * from, so the two agree.
 */
function varDefault(varKey: LocationVarKey): string {
  return VarDef[VarsHelper.varKeyForType(VarType.Location, varKey)]?.defaultValue ?? "";
}

function detailsSetting(discount: DiscountDef): CortexSetting {
  return {
    kind: "textarea",
    key: `${discount.key}_details`,
    varKey: discount.detailsVarKey,
    label: discount.label,
    description: `${discount.description} Cover the amount, who qualifies, how to claim it, and any limits — the bot relays this wording when a customer asks.`,
    placeholder: `e.g. ${DETAILS_PLACEHOLDERS[discount.key]}`,
    rows: 3,
    default: varDefault(discount.detailsVarKey)
  };
}

function nameSetting(discount: DiscountDef, nameVarKey: LocationVarKey): CortexSetting {
  return {
    kind: "text",
    key: `${discount.key}_name`,
    varKey: nameVarKey,
    label: `${discount.label} name`,
    description: "Optional. The bot calls the discount this instead of the default label.",
    placeholder: `e.g. ${discount.label}`,
    default: varDefault(nameVarKey)
  };
}

/**
 * One row per discount, plus a display-name row for any discount that carries a
 * name override. The name row follows its own discount so the two read together.
 */
const SETTINGS: CortexSetting[] = DISCOUNTS.flatMap((discount) =>
  discount.nameVarKey ? [detailsSetting(discount), nameSetting(discount, discount.nameVarKey)] : [detailsSetting(discount)]
);

export function DiscountsTopic(): React.ReactElement {
  const ctx = useTopicContext();
  const page = usePropertiesGetLocationIds();
  // What a field falls back to once its override here is deleted: whatever the
  // next scope up resolves to. At org scope there's nothing above, so the
  // fallback is the VarDef default instead.
  const isOrgScope = page.type === LocationScope.ORG;
  const parentVars = useMergedLocationVariables(TOPIC_VARS, page.locationId != null ? page.groupId : undefined, undefined);
  // The worksheet owns the page-level editing flag; this narrows it to the card.
  const [isCardOpen, setIsCardOpen] = useState<boolean>(false);

  // Save / Cancel clears the worksheet's flag — close the card to match.
  useEffect(() => {
    if (!ctx.isEditing && isCardOpen) setIsCardOpen(false);
  }, [ctx.isEditing, isCardOpen]);

  const handleEdit = (): void => {
    setIsCardOpen(true);
    ctx.setEditing(true);
  };

  const isEditing = ctx.isEditing && isCardOpen;

  return (
    <div className="flex flex-col gap-4">
      <section className="overflow-hidden rounded-lg border border-grey-200 bg-white">
        <div className="flex items-center justify-between gap-4 px-4 py-3">
          <div className="min-w-0">
            <h3 className="heading-base-alt text-grey-900">Discounts</h3>
            <p className="body-sm mt-0.5 text-grey-600">Leave a discount blank and the bot will say your facility doesn't offer it.</p>
          </div>
          {isEditing ? (
            <div className="flex shrink-0 items-center gap-2">
              <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} disabled={ctx.saving} onClick={ctx.cancelEdits}>
                Cancel
              </Button>
              <Button color={ButtonColor.ORANGE} size={ButtonSize.SMALL} disabled={ctx.saving} onClickAsync={ctx.saveTopic}>
                {ctx.saving ? "Saving…" : "Save"}
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleEdit}
              className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
            >
              Edit
              <BiEditAlt className="size-4" />
            </button>
          )}
        </div>

        <TopicProvider {...ctx} isEditing={isEditing}>
          <div className="border-t border-grey-200">
            {SETTINGS.map((setting) => (
              <DiscountRow
                key={setting.key}
                setting={setting}
                isOrgScope={isOrgScope}
                parentInheritance={parentVars?.[setting.varKey]?.inheritance}
                revertsTo={(isOrgScope ? String(setting.default ?? "") : (parentVars?.[setting.varKey]?.value ?? "")) || "(empty)"}
              />
            ))}
          </div>
        </TopicProvider>
      </section>
    </div>
  );
}

// ─── Row ───────────────────────────────────────────────────────────────────

interface DiscountRowProps {
  setting: CortexSetting;
  /** Org is the top scope — a revert there lands on the VarDef default. */
  isOrgScope: boolean;
  /** How the next scope up resolves — names the revert's destination. */
  parentInheritance: LocationVarInheritance | undefined;
  /** Display text for the value a revert would restore. */
  revertsTo: string;
}

function DiscountRow({ setting, isOrgScope, parentInheritance, revertsTo }: DiscountRowProps): React.ReactElement {
  const { merged, draft } = useTopicRow(setting.varKey);
  const handlers = useTopicHandlers(setting.varKey);
  const { isEditing } = useTopicContext();

  const draftValue = draft?.value;
  const overrideStaged = draftValue !== undefined;
  const isOverride = merged.inheritance === LocationVarInheritance.NONE;
  const displayValue = draftValue ?? merged.value ?? String(setting.default ?? "");

  const setValue = (value: string): void => handlers.onSettingValue(value);

  const revertLabel = revertLabelFor({ isOrgScope, isOverride, inheritance: merged.inheritance, parentInheritance });

  const handleRevert = (): void => {
    if (!window.confirm(`${revertLabel}? This will discard the current customization for this field.`)) return;
    // A saved override is deleted server-side, which also clears this row's
    // draft. With nothing saved yet, dropping the draft is the whole job.
    if (isOverride) void handlers.onRevert(merged);
    else handlers.onDiscard();
  };

  // mx rather than px so the row's border stops short of the card edge, lining
  // the divider up with the label and the tag.
  return (
    <div className="mx-4 grid grid-cols-[200px_minmax(0,1fr)_auto] items-center gap-x-4 border-b border-grey-200 py-3 last:border-b-0">
      <span className="body-sm-alt text-grey-900">{setting.label}</span>

      {/* The control gets a column of its own so the description below can share
          it and match its width exactly, rather than running under the tag. */}
      <div className="min-w-0">
        {!isEditing ? (
          <p className="body-sm break-words whitespace-pre-wrap text-grey-900">{displayValue || "—"}</p>
        ) : setting.kind === "textarea" ? (
          <TextArea
            // A textarea is inline-block by default, so its container picks up
            // a text line box and leaves phantom space underneath.
            className="block"
            value={displayValue}
            rows={setting.rows}
            placeholder={setting.placeholder}
            onChange={(e) => setValue(e.target.value)}
          />
        ) : (
          <Input
            value={displayValue}
            placeholder={setting.kind === "text" ? setting.placeholder : undefined}
            onChange={(e) => setValue(e.target.value)}
          />
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <CortexInheritancePill inheritance={isOverride || overrideStaged ? LocationVarInheritance.NONE : merged.inheritance} />

        {isEditing && (isOverride || overrideStaged) && (
          <Tooltip content={`Reverts to: ${revertsTo}`}>
            <button
              type="button"
              onClick={handleRevert}
              className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
            >
              {revertLabel}
              <GrRevert className="size-4" />
            </button>
          </Tooltip>
        )}
      </div>

      {/* Second grid row, control column — so the description matches the
          control's width and doesn't drag the label off-centre from it. */}
      {isEditing && setting.description && <p className="body-sm col-start-2 mt-1 text-grey-600">{setting.description}</p>}
    </div>
  );
}
